import { isEmpty } from 'lodash';
import { notify, useTrackWithUser } from '@ion/api';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import useFeatureEnabled from 'app/launch-darkly/use-feature-enabled';
import downloadAnalyticsFile from './export-import/downloadAnalyticsFile';
import getFormData from './getFormData';
import buildSettingsTemplate from './buildSettingsTemplate';
import getCrossDomainSettings from './getCrossDomainSettings';

/**
 * Custom hook to handle Analytics File download functionality
 *
 * @param {object} params - Hook parameters
 * @param {object} params.formState - Current form state
 * @param {object} params.analyticsFileData - Current analytics file data
 * @param {array} params.stagedIdentitySyncsData - Staged identity syncs data
 * @param {array} params.stagedIntegrationsData - Staged integrations data
 * @param {string} params.writekey - Pipeline writekey
 * @param {array} params.consentCategories - Consent categories
 * @param {string} params.pipelineName - Pipeline name
 * @returns {object} Hook interface
 */
export default function useAnalyticsFileDownload({
  formState,
  analyticsFileData,
  stagedIdentitySyncsData,
  stagedIntegrationsData,
  writekey,
  consentCategories,
  pipelineName,
}) {
  const { ajsOptimizeFileBuildEnabled, builderServerQueueTasksEnabled, ecmConfigurableConsentEnabled } =
    useFeatureEnabled(
      FEATURE_FLAGS.ajsOptimizeFileBuild,
      FEATURE_FLAGS.builderServerQueueTasks,
      FEATURE_FLAGS.ecmConfigurableConsent
    );

  const [trackDownload] = useTrackWithUser('analytics_file_downloaded');

  const handleDownload = () => {
    try {
      const data = getFormData(formState);
      const crossDomainSettings = getCrossDomainSettings(analyticsFileData);

      const settingsTemplate = buildSettingsTemplate({
        stagedIdentitySyncsData,
        stagedIntegrationsData,
        data,
        writekey,
        crossDomainSettings,
        optimizeBuild: ajsOptimizeFileBuildEnabled,
        consentCategories,
        queueTasks: builderServerQueueTasksEnabled,
        hasConsentModule: ecmConfigurableConsentEnabled,
        // Same gate as the build path — the payload shape must not depend on
        // which stale keys formState happens to hold.
      });

      downloadAnalyticsFile({ settingsTemplate, pipelineName });
      trackDownload();
      notify.success({ message: 'Analytics File downloaded successfully' });
    } catch (exception) {
      const errorMessage = isEmpty(exception.message) ? 'Failed to download Analytics File' : exception.message;
      notify.error({ message: errorMessage });
    }
  };

  return {
    handleDownload,
  };
}
